import React, { useEffect, Fragment, useContext, useMemo } from "react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    PlusIcon,
    ChevronDownIcon,
    XMarkIcon,
    NoSymbolIcon,
    FunnelIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import Table from "../../components/Table";
import InlineCheckbox from "../../components/InlineCheckbox";
import SelectInput from "../../components/SelectInput";
import DateInput from "../../components/DateInput";
import RadioButtons from "../../components/RadioButtons";
import Tag from "../../components/Tag";
import { Dialog, Popover, Transition, Combobox } from "@headlessui/react";

const columns = [
    {
        name: "name",
        displayText: "Nombre y Apellido",
        clickable: true,
        truncate: true,
    },
    { name: "govId", displayText: "C.I." },
    { name: "age", displayText: "Edad" },
    { name: "sex", displayText: "Sexo" },
    { name: "city", displayText: "Ciudad    " },
    { name: "lastConsultationDate", displayText: "Última visita" },
];

const sex = ["Masculino", "Femenino"];

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function PatientPage() {
    const [patientsError, setPatientsError] = useState("");
    const [patients, setPatients] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [search, setSearch] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [cities, setCities] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 20;
    const [showFilters, setShowFilters] = useState(false);
    const [diagnoses, setDiagnoses] = useState([]);
    const [diagnosisQuery, setDiagnosisQuery] = useState("");
    const [filters, setFilters] = useState({
        diagnosis: [],
        dm: false,
        hta: false,
    });
    const [activeFilters, setActiveFilters] = useState({
        diagnosis: [],
        dm: false,
        hta: false,
    });

    const citiesMap = useMemo(() => {
        return new Map(cities.map((city) => [city.id, city.name]));
    }, [cities]);

    const filteredDiagnoses = useMemo(() => {
        if (!diagnosisQuery) return diagnoses;
        return diagnoses.filter((diagnosis) =>
            diagnosis.name.toLowerCase().includes(diagnosisQuery.toLowerCase()),
        );
    }, [diagnoses, diagnosisQuery]);

    // getting initial data
    useEffect(() => {
        setLoading(false);
        api.cities.getMulti().then((response) => {
            setCities(response);
        });
        api.diagnostics.getMulti().then((response) => {
            setDiagnoses(response);
        });
    }, []);

    useEffect(() => {
        if (search) {
            setCurrentPage(1);
        }
    }, [search]);

    useEffect(() => {
        const hasActiveFilters =
            activeFilters.diagnosis.length > 0 ||
            activeFilters.dm ||
            activeFilters.hta;

        if (search || hasActiveFilters) {
            setLoading(true);
            const offset = (currentPage - 1) * pageSize;
            const filterParams = { page: currentPage };
            if (activeFilters.diagnosis.length > 0) {
                filterParams.diagnosis = activeFilters.diagnosis.join(",");
            }
            if (activeFilters.dm) filterParams.dm = "true";
            if (activeFilters.hta) filterParams.hta = "true";

            api.patients
                .getMulti(
                    "alphabetic",
                    search || null,
                    pageSize,
                    offset,
                    filterParams,
                )
                .then(
                    (response) => (
                        setPatients(
                            response.results
                                .filter((array) => {
                                    return array.firstName ? true : false;
                                })
                                .map((element) => {
                                    return {
                                        ...element,
                                        name:
                                            element.firstName +
                                            " " +
                                            element.lastName,
                                        city: citiesMap.get(element.city),
                                        sex:
                                            element.sex === 1
                                                ? "Masculino"
                                                : "Femenino",
                                    };
                                }),
                        ),
                        setTotal(response.count),
                        setLoading(false)
                    ),
                );
        } else if (search === "" && !hasActiveFilters) {
            setPatients([]);
            setTotal(0);
            setCurrentPage(1);
        }
    }, [search, currentPage, citiesMap, activeFilters]);

    const handleApplyFilters = () => {
        setActiveFilters(filters);
        setShowFilters(false);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        const emptyFilters = {
            diagnosis: [],
            dm: false,
            hta: false,
        };
        setFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setShowFilters(false);
    };

    const toggleDiagnosis = (diagnosisId) => {
        setFilters((prev) => ({
            ...prev,
            diagnosis: prev.diagnosis.includes(diagnosisId)
                ? prev.diagnosis.filter((id) => id !== diagnosisId)
                : [...prev.diagnosis, diagnosisId],
        }));
    };

    const hasActiveFilters =
        activeFilters.diagnosis.length > 0 ||
        activeFilters.dm ||
        activeFilters.hta;

    return (
        <div className="bg-gray-100 h-screen">
            <Sidebar
                currentScreen={"Pacientes"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
            />
            <div className="md:pl-64 flex flex-col h-screen flex-1">
                {loading ? (
                    <div className="flex h-full items-center justify-center ">
                        <div className="w-16 h-16 border-b-2 border-blue-900 rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <>
                        <div className="bg-gray-100 z-10">
                            <Header
                                value={search}
                                search={true}
                                setSearch={setSearch}
                                setSidebarOpen={setSidebarOpen}
                                searchPlaceholder="Buscar por nombre o número de documento."
                                buttonText={
                                    patients.length ? "Nuevo paciente" : null
                                }
                                href="/patients/new"
                                filtersButton={
                                    <button
                                        onClick={() => setShowFilters(true)}
                                        className={classNames(
                                            hasActiveFilters
                                                ? "bg-blue-600 text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-50",
                                            "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                                        )}
                                    >
                                        <FunnelIcon className="-ml-1 mr-2 h-5 w-5" />
                                        Filtros avanzados
                                        {hasActiveFilters && (
                                            <span className="ml-2 bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-semibold">
                                                {(activeFilters.diagnosis
                                                    .length || 0) +
                                                    (activeFilters.dm ? 1 : 0) +
                                                    (activeFilters.hta ? 1 : 0)}
                                            </span>
                                        )}
                                    </button>
                                }
                            />
                            {patientsError && (
                                <div className="mb-3 mx-10 text-sm text-red-600">
                                    {patientsError}
                                </div>
                            )}
                        </div>
                        <main className="flex-1 bg-gray-100 overflow-y-auto">
                            {patients.length ? (
                                <div className="pt-4 pb-10">
                                    <div className="max-w-7xl mx-auto ml-2 px-4 mb-4 sm:px-6 md:px-8">
                                        <h1 className="text-2xl font-bold leading-9 text-gray-600">
                                            Total de pacientes registrados:{" "}
                                            {total}
                                        </h1>
                                    </div>
                                    <div className="px-4 sm:px-6 md:px-8">
                                        <Table
                                            module="patients"
                                            actiontext="Ver"
                                            columns={columns}
                                            rows={patients}
                                            pagination={{
                                                currentPage,
                                                totalPages: Math.ceil(
                                                    total / pageSize,
                                                ),
                                                total,
                                                start:
                                                    (currentPage - 1) *
                                                        pageSize +
                                                    1,
                                                end: Math.min(
                                                    currentPage * pageSize,
                                                    total,
                                                ),
                                                pages: Array.from(
                                                    {
                                                        length: Math.min(
                                                            Math.ceil(
                                                                total /
                                                                    pageSize,
                                                            ),
                                                            7,
                                                        ),
                                                    },
                                                    (_, i) => {
                                                        const totalPages =
                                                            Math.ceil(
                                                                total /
                                                                    pageSize,
                                                            );
                                                        if (totalPages <= 7)
                                                            return i + 1;
                                                        if (currentPage <= 4)
                                                            return i + 1;
                                                        if (
                                                            currentPage >=
                                                            totalPages - 3
                                                        )
                                                            return (
                                                                totalPages -
                                                                6 +
                                                                i
                                                            );
                                                        return (
                                                            currentPage - 3 + i
                                                        );
                                                    },
                                                ),
                                                onPageChange: (page) =>
                                                    setCurrentPage(page),
                                                onPrevious: () =>
                                                    setCurrentPage((prev) =>
                                                        Math.max(1, prev - 1),
                                                    ),
                                                onNext: () =>
                                                    setCurrentPage((prev) =>
                                                        Math.min(
                                                            Math.ceil(
                                                                total /
                                                                    pageSize,
                                                            ),
                                                            prev + 1,
                                                        ),
                                                    ),
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex h-full">
                                    <div className="mx-auto my-auto text-center">
                                        <NoSymbolIcon
                                            className="h-12 w-12 mx-auto text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <h3 className="mt-2 text-sm font-semibold text-gray-900">
                                            No se encontraron resultados
                                        </h3>
                                        <p className="mt-1 text-sm font-semibold text-gray-500">
                                            Probá con otros datos o registrá un
                                            nuevo paciente.
                                        </p>
                                        <div className="mt-6">
                                            <Link
                                                to="/patients/new"
                                                type="button"
                                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                                            >
                                                <PlusIcon
                                                    className="-ml-1 mr-2 h-5 w-5"
                                                    aria-hidden="true"
                                                />
                                                Nuevo paciente
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </main>
                    </>
                )}
            </div>

            {/* Filters Modal */}
            <Transition.Root show={showFilters} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={setShowFilters}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-lg font-semibold leading-6 text-gray-900"
                                            >
                                                Filtros avanzados
                                            </Dialog.Title>
                                            <button
                                                onClick={() =>
                                                    setShowFilters(false)
                                                }
                                                className="text-gray-400 hover:text-gray-500"
                                            >
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        <div className="space-y-6">
                                            {/* Diagnosis Filter */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Diagnóstico
                                                </label>
                                                <Combobox
                                                    value={null}
                                                    onChange={(selected) => {
                                                        if (selected) {
                                                            setFilters(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    diagnosis:
                                                                        prev.diagnosis.includes(
                                                                            selected,
                                                                        )
                                                                            ? prev.diagnosis.filter(
                                                                                  (
                                                                                      id,
                                                                                  ) =>
                                                                                      id !==
                                                                                      selected,
                                                                              )
                                                                            : [
                                                                                  ...prev.diagnosis,
                                                                                  selected,
                                                                              ],
                                                                }),
                                                            );
                                                            setDiagnosisQuery(
                                                                "",
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <div className="relative">
                                                        <Combobox.Input
                                                            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                                                            placeholder="Buscar diagnóstico..."
                                                            value={
                                                                diagnosisQuery
                                                            }
                                                            onChange={(event) =>
                                                                setDiagnosisQuery(
                                                                    event.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                                            <ChevronDownIcon
                                                                className="h-5 w-5 text-gray-400"
                                                                aria-hidden="true"
                                                            />
                                                        </Combobox.Button>

                                                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                            {filteredDiagnoses.length ===
                                                                0 &&
                                                            diagnosisQuery !==
                                                                "" ? (
                                                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                                                    No se
                                                                    encontraron
                                                                    diagnósticos.
                                                                </div>
                                                            ) : (
                                                                filteredDiagnoses.map(
                                                                    (
                                                                        diagnosis,
                                                                    ) => (
                                                                        <Combobox.Option
                                                                            key={
                                                                                diagnosis.id
                                                                            }
                                                                            value={
                                                                                diagnosis.id
                                                                            }
                                                                            className={({
                                                                                active,
                                                                            }) =>
                                                                                classNames(
                                                                                    "relative cursor-pointer select-none py-2 pl-3 pr-9",
                                                                                    active
                                                                                        ? "bg-blue-600 text-white"
                                                                                        : "text-gray-900",
                                                                                )
                                                                            }
                                                                        >
                                                                            {({
                                                                                active,
                                                                                selected,
                                                                            }) => (
                                                                                <>
                                                                                    <span
                                                                                        className={classNames(
                                                                                            "block truncate",
                                                                                            selected &&
                                                                                                "font-semibold",
                                                                                        )}
                                                                                    >
                                                                                        {
                                                                                            diagnosis.name
                                                                                        }
                                                                                    </span>
                                                                                    {selected && (
                                                                                        <span
                                                                                            className={classNames(
                                                                                                "absolute inset-y-0 right-0 flex items-center pr-4",
                                                                                                active
                                                                                                    ? "text-white"
                                                                                                    : "text-blue-600",
                                                                                            )}
                                                                                        >
                                                                                            ✓
                                                                                        </span>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </Combobox.Option>
                                                                    ),
                                                                )
                                                            )}
                                                        </Combobox.Options>
                                                    </div>
                                                </Combobox>
                                                {filters.diagnosis.length >
                                                    0 && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {filters.diagnosis.map(
                                                            (diagId) => {
                                                                const diag =
                                                                    diagnoses.find(
                                                                        (d) =>
                                                                            d.id ===
                                                                            diagId,
                                                                    );
                                                                return diag ? (
                                                                    <span
                                                                        key={
                                                                            diagId
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-700"
                                                                    >
                                                                        {
                                                                            diag.name
                                                                        }
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setFilters(
                                                                                    (
                                                                                        prev,
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        diagnosis:
                                                                                            prev.diagnosis.filter(
                                                                                                (
                                                                                                    id,
                                                                                                ) =>
                                                                                                    id !==
                                                                                                    diagId,
                                                                                            ),
                                                                                    }),
                                                                                );
                                                                            }}
                                                                            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200"
                                                                        >
                                                                            <XMarkIcon className="h-3 w-3" />
                                                                        </button>
                                                                    </span>
                                                                ) : null;
                                                            },
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Medical Conditions */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Condiciones médicas
                                                </label>
                                                <div className="space-y-2">
                                                    <div className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            id="dm"
                                                            checked={filters.dm}
                                                            onChange={(e) =>
                                                                setFilters(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        dm: e
                                                                            .target
                                                                            .checked,
                                                                    }),
                                                                )
                                                            }
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <label
                                                            htmlFor="dm"
                                                            className="ml-3 text-sm text-gray-700 cursor-pointer"
                                                        >
                                                            Diabetes (DM)
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            id="hta"
                                                            checked={
                                                                filters.hta
                                                            }
                                                            onChange={(e) =>
                                                                setFilters(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        hta: e
                                                                            .target
                                                                            .checked,
                                                                    }),
                                                                )
                                                            }
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <label
                                                            htmlFor="hta"
                                                            className="ml-3 text-sm text-gray-700 cursor-pointer"
                                                        >
                                                            Hipertensión (HTA)
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 flex gap-3 justify-end">
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Limpiar filtros
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleApplyFilters}
                                            className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Aplicar filtros
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
}
